// Components
import Alert from '@amzn/awsui-components-react/polaris/alert';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import Form from '@amzn/awsui-components-react/polaris/form';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';
import Modal, { ModalProps } from '@amzn/awsui-components-react/polaris/modal';
import RadioGroup from '@amzn/awsui-components-react/polaris/radio-group';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
// Hooks
import { useEffect, useRef, useState } from 'react';
import { useAttendance, useTrackTime } from '../../hooks/attendance/attendanceMutation';
import {
  useParticipantSessionStorage,
  ParamsParticipantData,
} from '../../hooks/attendance/participantSessionStorage';
import {
  useBulkParticipantSessionStorage,
  BulkParticipantData,
} from '../../hooks/attendance/bulkParticipantSessionStorage';
import { useLocalizedText } from '../../hooks/localizedText';
import { useErrorMessages } from '../../hooks/localization/errorMessages';
import { useUser } from '../../hooks/user';
// Types
import { Shift, AttendanceStatus, ShiftTimingType } from '@amzn/red-velvet-api';
import { Amazonian } from '../../utils/attendance/peopleSearch';
import { calculateShiftTime } from '../../hooks/shifts/shiftTime';
import {
  MAX_SHIFT_HOURS,
  MIN_PER_HOUR,
  SHORT_SHIFT_ALLOWED_EXTRA_HOURS,
  SHORT_SHIFT_HOURS,
} from '../../utils/constants';

// Two distinct usage modes:
// 1. Self-tracking (API save): User tracks their own time via API
// 2. Bulk tracking (URL save): Coordinator tracks time for selected people via session storage

interface BaseTimeModalProps {
  shifts: Shift[]; // Single element for single-shift, multiple for event-level
  vcCreated?: boolean;
  visible: boolean;
  onDismiss: NonNullable<ModalProps['onDismiss']>;
}

// Mode 1: Self-tracking via API (used in shift details page)
interface SelfTrackingProps extends BaseTimeModalProps {
  saveToUrl?: false;
  selectedItems?: never;
  isEventLevelTracking?: never;
  /**
   * Self-track modal variant for the VolunteerEventShiftsTable (ShiftWaitlist-gated):
   *  - 'attendance'    -> Attended (+ time) / Did not attend radio, "Save attendance".
   *  - 'volunteerTime' -> time only, "Save volunteer time".
   * Omitted keeps the default "Track Volunteer Time" behavior (carousel / My Activity), unchanged.
   */
  attendanceMode?: 'attendance' | 'volunteerTime';
}

// Mode 2: Bulk tracking via session storage (used in coordinator attendance table)
interface BulkTrackingProps extends BaseTimeModalProps {
  saveToUrl: true;
  selectedItems: readonly Amazonian[];
  isEventLevelTracking: boolean;
}

export type TimeModalProps = SelfTrackingProps | BulkTrackingProps;

/**
 * Formats minutes into HH:MM string format
 * @param minutes - Total minutes to format
 * @returns Formatted time string (e.g., "02:30")
 */
export function formatMinutes(minutes: number) {
  const hours = Math.floor(minutes / MIN_PER_HOUR);
  const mins = minutes % MIN_PER_HOUR;

  return `${hours < 10 ? '0' + hours : hours}:${mins < 10 ? '0' + mins : mins}`;
}

export function parseTime(rawTime: string, shiftMins: number) {
  let hours = 0;
  let min = 0;

  let error = '';
  let warning = '';

  const timeChunks = rawTime.split(':');
  if (timeChunks) {
    if (timeChunks.length > 0) {
      hours = timeChunks[0] ? parseInt(timeChunks[0]) : 0;
    }

    if (timeChunks.length > 1) {
      min = timeChunks[1] ? parseInt(timeChunks[1]) : 0;
    }
  }

  if (hours === 0 && min === 0) {
    error = 'timeModal.noTime';
  }

  const minutesReported = hours * MIN_PER_HOUR + min;

  if (shiftMins !== 0 && minutesReported > MAX_SHIFT_HOURS * MIN_PER_HOUR) {
    error = 'timeModal.exceededMaxAttendanceTime';
  } else if (
    shiftMins !== 0 &&
    shiftMins <= SHORT_SHIFT_HOURS * MIN_PER_HOUR &&
    minutesReported - shiftMins >= SHORT_SHIFT_ALLOWED_EXTRA_HOURS * MIN_PER_HOUR
  ) {
    warning = 'timeModal.shortShiftHighTime';
  } else if (
    minutesReported > SHORT_SHIFT_HOURS * MIN_PER_HOUR &&
    minutesReported <= MAX_SHIFT_HOURS * MIN_PER_HOUR
  ) {
    warning = 'timeModal.longShiftHighTime';
  } else if (minutesReported > MAX_SHIFT_HOURS * MIN_PER_HOUR) {
    error = 'timeModal.exceededMaxShiftTime';
  } else if (min >= MIN_PER_HOUR) {
    hours += Math.floor(min / MIN_PER_HOUR);
    min = min % MIN_PER_HOUR;
    warning = 'timeModal.tooManySections';
  } else if (timeChunks.length > 2) {
    warning = 'timeModal.tooManySections';
  }

  return {
    hours,
    min,
    time: formatMinutes(minutesReported),
    minutesReported,
    error,
    warning,
  };
}

/**
 * Hook that parses time input and provides validation errors/warnings
 * @param shiftMins - Shift length in minutes for validation rules
 * @returns Parser function and error/warning state
 */
function useParsedTimeWithWarningsAndErrors(shiftMins: number) {
  const { t } = useLocalizedText(['shifts', 'translation']);

  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  return {
    parseTimeWithWarningsAndErrors: (rawTime: string) => {
      const parsed = parseTime(rawTime, shiftMins);

      if (parsed.error) {
        setError(t(parsed.error, { hours: parsed.hours, min: parsed.min }));
      } else {
        // We need to reset the error to a nullish value if there is no error
        // to ensure the modal works properly
        setError('');
      }
      if (parsed.warning) {
        setWarning(t(parsed.warning, { hours: parsed.hours, min: parsed.min }));
      } else {
        // We need to reset the warning to a nullish value if there is no warning
        // to ensure the modal works properly
        setWarning('');
      }

      return parsed.minutesReported;
    },
    error,
    setError,
    warning,
    setWarning,
  };
}

/**
 * Hook that manages attendance tracking logic for both API and URL save modes
 * @param shifts - Array of shifts (single element for single-shift mode, multiple for event-level)
 * @param onDismiss - Modal dismiss handler
 * @param selectedItems - People to track time for (bulk mode only)
 * @param isEventLevelTracking - Whether tracking at event-level vs single-shift
 * @returns Time state, validation, and save functions for both modes
 */
function useUpdateAttendance(
  shifts: Shift[],
  onDismiss: NonNullable<ModalProps['onDismiss']>,
  selectedItems: readonly Amazonian[],
  isEventLevelTracking: boolean,
  vcCreated?: boolean
) {
  const { t } = useLocalizedText(['shifts', 'translation']);
  const { resolveError } = useErrorMessages();
  const user = useUser();

  const singleShift = shifts[0];
  const attendance = useAttendance(singleShift?.eventId, singleShift?.shiftId, user.userId);

  // For event-level, calculate the shortest shift length among selected items
  // This ensures warnings/errors are shown if time is too high for any selected shift
  let shiftMins = 0;
  if (isEventLevelTracking && selectedItems) {
    const selectedShiftIds = new Set(selectedItems.map((item) => item.shiftId).filter(Boolean));
    const selectedShifts = shifts.filter((s) => s.shiftId && selectedShiftIds.has(s.shiftId));

    if (selectedShifts.length > 0) {
      const shiftLengths = selectedShifts
        .map((s) => calculateShiftTime(s, vcCreated))
        .filter((len) => len > 0);

      // Use shortest shift for validation (most restrictive)
      shiftMins = shiftLengths.length > 0 ? Math.min(...shiftLengths) : 0;
    }
  } else {
    // Single-shift: use the first (and only) shift for validation
    // Returns shiftLength for vcCreated flexible shifts, date math otherwise
    shiftMins = calculateShiftTime(singleShift, vcCreated);
    // Don't pre-fill for shifts > 8 hours so coordinators enter time manually.
    // Exempt flexible shifts with valid shiftLength (actual volunteering duration, not the window).
    // shiftLength>0 is defensive: create event wizard validates this, but guards against bad data.
    if (
      shiftMins > SHORT_SHIFT_HOURS * MIN_PER_HOUR &&
      !(
        vcCreated &&
        singleShift?.shiftTimingType === ShiftTimingType.FLEXIBLE &&
        singleShift?.shiftLength &&
        singleShift.shiftLength > 0
      )
    ) {
      shiftMins = 0;
    }
  }

  const [time, setTime] = useState(
    attendance.data
      ? formatMinutes(attendance.data.minutes)
      : shiftMins
        ? formatMinutes(shiftMins)
        : ''
  );

  // Self-tracking mode requires eventId and shiftId for API calls
  // For bulk tracking mode or unlisted events, provide empty strings (mutation won't be used)
  const timeMutation = useTrackTime(singleShift?.eventId || '', singleShift?.shiftId || '');

  const { error, setError, warning, setWarning, parseTimeWithWarningsAndErrors } =
    useParsedTimeWithWarningsAndErrors(shiftMins);

  const { updateParticipants } = useParticipantSessionStorage();
  const { updateBulkParticipants, updateBulkParticipantsFromParticipantData } =
    useBulkParticipantSessionStorage();

  // Self-tracking: User tracks their own time via API in Volunteer Activity page
  // Uses shift.eventId, shift.shiftId, and user.userId (not selectedItems)
  const updateAttendanceWithApi = () => {
    if (!singleShift) return; // Guard: only for self-tracking mode with valid shift

    const minutes = parseTimeWithWarningsAndErrors(time);
    if (minutes !== 0) {
      timeMutation.mutate(
        {
          eventId: singleShift.eventId,
          shiftId: singleShift.shiftId,
          alias: user.userId,
          minutes,
          attendanceStatus: AttendanceStatus.ATTENDED,
        },
        {
          onError: (error) => {
            setError(t('timeModal.error', { error: resolveError(error) }));
          },
          onSuccess: () => {
            // Note: the 'closeButton' input here indicates that the modal was closed by an action within the modal
            // since the dismiss function only accepts the following reasons: 'closeButton', 'overlay', 'keyboard'
            // and overlay and keyboard indicate user events that are external to the modal
            onDismiss(new CustomEvent<ModalProps.DismissDetail>('closeButton'));
          },
        }
      );
    }
  };

  // Bulk tracking: Coordinator tracks time for selected people via session storage
  // Used in coordinator attendance table when saveToUrl=true
  // Supports both single-shift (alias keys) and event-level (alias-shiftId keys)
  const updateAttendanceInUrl = () => {
    const minutes = parseTimeWithWarningsAndErrors(time);
    if (minutes !== 0) {
      if (isEventLevelTracking && selectedItems) {
        // Event-level: use composite keys 'alias-shiftId' to target specific shift
        // Each person can be in multiple shifts, so we track time per person per shift
        const bulkUpdates: BulkParticipantData = {};
        selectedItems.forEach((item) => {
          if (item.alias && item.shiftId) {
            const key = `${item.alias}-${item.shiftId}`;
            bulkUpdates[key] = {
              alias: item.alias,
              name: item.name,
              building: item.building,
              managerLogin: item.managerLogin,
              shiftId: item.shiftId,
              originalShiftId: item.originalShiftId || item.shiftId,
              shiftName: item.shiftName,
              shiftDate: item.shiftDate,
              shiftTime: item.shiftTime,
              signedUp: item.signedUp,
              trackedBy: item.trackedBy,
              attendance: 'ATTENDED',
              trackedTime: minutes,
            };
          }
        });
        updateBulkParticipants(bulkUpdates);
      } else {
        // Single-shift: use simple alias keys since there's only one shift per person
        const mixin: ParamsParticipantData = {};
        selectedItems.forEach((item) => {
          if (item.alias) {
            mixin[item.alias] = {
              attendance: 'ATTENDED',
              trackedTime: minutes,
            };
          }
        });
        updateParticipants(mixin);
        updateBulkParticipantsFromParticipantData(mixin);
      }
      onDismiss(new CustomEvent<ModalProps.DismissDetail>('closeButton'));
    }
  };

  // Self-track "Did not attend" — records a zero-minute DID_NOT_ATTEND attendance (Track attendance modal).
  const saveDidNotAttend = () => {
    if (!singleShift) return;
    timeMutation.mutate(
      {
        eventId: singleShift.eventId,
        shiftId: singleShift.shiftId,
        alias: user.userId,
        minutes: 0,
        attendanceStatus: AttendanceStatus.DID_NOT_ATTEND,
      },
      {
        onError: (error) => {
          setError(t('timeModal.error', { error: resolveError(error) }));
        },
        onSuccess: () => {
          onDismiss(new CustomEvent<ModalProps.DismissDetail>('closeButton'));
        },
      }
    );
  };

  return {
    error,
    warning,
    pending: timeMutation.isPending,
    time,
    setTime,
    parseTimeWithWarningsAndErrors,
    setError,
    setWarning,
    updateAttendanceWithApi,
    updateAttendanceInUrl,
    saveDidNotAttend,
    existingAttendanceStatus: attendance.data?.attendanceStatus,
    existingMinutes: attendance.data?.minutes,
  };
}

/**
 * TimeModal component for tracking attendance time
 * Supports two modes:
 * 1. Self-tracking: User tracks their own time via API (shift details page)
 * 2. Bulk tracking: Coordinator tracks time for selected people via session storage (attendance table)
 */
export function TimeModal(props: TimeModalProps & ModalProps) {
  const { t } = useLocalizedText(['shifts', 'translation']);

  // For self-tracking (API save), selectedItems is not used - only user.userId is needed
  // The default value satisfies TypeScript but won't be used in updateAttendanceWithApi
  const {
    error,
    warning,
    pending,
    time,
    setTime,
    parseTimeWithWarningsAndErrors,
    setError,
    setWarning,
    updateAttendanceWithApi,
    updateAttendanceInUrl,
    saveDidNotAttend,
    existingAttendanceStatus,
    existingMinutes,
  } = useUpdateAttendance(
    props.shifts,
    props.onDismiss,
    props.selectedItems || [],
    props.isEventLevelTracking || false,
    props.vcCreated
  );

  // attendanceMode only applies to self-track (saveToUrl is false). 'attendance' adds the
  // Attended / Did not attend radio; both new modes only relabel the modal vs the default self-track
  // ("Track Volunteer Time"), which is left unchanged so the carousel / My Activity are unaffected.
  const attendanceMode = props.saveToUrl ? undefined : props.attendanceMode;
  const isAttendanceChoice = attendanceMode === 'attendance';
  const [attended, setAttended] = useState<boolean | undefined>(
    existingAttendanceStatus === undefined
      ? undefined
      : existingAttendanceStatus === AttendanceStatus.ATTENDED
  );

  // The time field + attended radio seed once via useState, but TimeModal stays mounted while only
  // `visible` toggles — so without this they would show a stale value when the gated modal is reopened
  // after an edit (the prefill bug). Re-seed from the latest attendance on the open transition only
  // (attendanceMode set => the new table's modal); the default self-track path is left untouched.
  // useAttendance stays mounted (TimeModal is rendered unconditionally), so useTrackTime's
  // update-vs-create decision stays warm; we only resync this local form state.
  const wasVisible = useRef(false);
  useEffect(() => {
    const justOpened = attendanceMode !== undefined && props.visible && !wasVisible.current;
    wasVisible.current = props.visible;
    if (!justOpened) return;
    setTime(existingMinutes != null ? formatMinutes(existingMinutes) : '');
    setAttended(
      existingAttendanceStatus === undefined
        ? undefined
        : existingAttendanceStatus === AttendanceStatus.ATTENDED
    );
    // Also clear any stale validation error/warning from a prior open (e.g. the user cleared the time
    // field then cancelled) so the reopened modal starts clean — matching the first-open state.
    setError('');
    setWarning('');
  }, [
    attendanceMode,
    props.visible,
    existingMinutes,
    existingAttendanceStatus,
    setTime,
    setError,
    setWarning,
  ]);

  const header = isAttendanceChoice
    ? t('timeModal.attendanceTitle')
    : attendanceMode === 'volunteerTime'
      ? t('timeModal.volunteerTimeTitle')
      : t('timeModal.title');
  const submitLabel = isAttendanceChoice
    ? t('timeModal.saveAttendance')
    : attendanceMode === 'volunteerTime'
      ? t('timeModal.saveVolunteerTime')
      : t('timeModal.trackTime');

  // In attendance mode the time input hides once the user explicitly picks "Did not attend".
  const showTimeInput = !isAttendanceChoice || attended !== false;
  const isTimeInvalid = error !== '' || time === '';
  const submitDisabled = isAttendanceChoice
    ? attended === undefined || (attended && isTimeInvalid)
    : isTimeInvalid;
  const onSubmit = () => {
    if (props.saveToUrl) return updateAttendanceInUrl();
    if (isAttendanceChoice && attended === false) return saveDidNotAttend();
    return updateAttendanceWithApi();
  };

  return (
    <Modal
      {...props}
      visible={props.visible}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={() =>
                props.onDismiss(new CustomEvent<ModalProps.DismissDetail>('closeButton'))
              }
            >
              {t('timeModal.cancel')}
            </Button>
            <Button
              variant="primary"
              loading={pending}
              disabled={submitDisabled}
              data-aci-analytics-name="shifts_reportTime"
              onClick={onSubmit}
            >
              {submitLabel}
            </Button>
          </SpaceBetween>
        </Box>
      }
      header={header}
      closeAriaLabel={t('timeModal.cancel')}
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <Form>
          <SpaceBetween size="s">
            {isAttendanceChoice && (
              <RadioGroup
                value={attended === undefined ? null : attended ? 'attended' : 'didNotAttend'}
                onChange={({ detail }) => {
                  setAttended(detail.value === 'attended');
                  // Clear any prior time-validation / save error so a stale message can't surface on
                  // the standalone Alert after switching to "Did not attend".
                  setError('');
                }}
                items={[
                  { value: 'attended', label: t('timeModal.attended') },
                  { value: 'didNotAttend', label: t('timeModal.didNotAttend') },
                ]}
              />
            )}
            {/* The error normally renders inside the time-input FormField; when that input is hidden
                (Did not attend), surface a failed save here so it isn't swallowed with the FormField. */}
            {error && !showTimeInput && <Alert type="error">{error}</Alert>}
            {showTimeInput && (
              <FormField
                label={t('timeModal.timeVolunteered')}
                warningText={warning}
                errorText={error}
              >
                <Input
                  onChange={({ detail }) => {
                    // Strip out anything other than digits or :
                    const rawTime = (detail.value.match(/[\d:]/g) || []).join('');
                    setTime(rawTime);
                    parseTimeWithWarningsAndErrors(rawTime);
                  }}
                  onBlur={() => parseTimeWithWarningsAndErrors(time)}
                  value={time}
                  placeholder="hh:mm"
                  ariaRequired
                  autoFocus={props.visible}
                  inputMode="numeric"
                  step={1}
                />
              </FormField>
            )}
          </SpaceBetween>
        </Form>
      </form>
    </Modal>
  );
}
